import type { ReactNode, TextareaHTMLAttributes } from 'react';

import resizerSrc from './assets/resizer.svg';
import styles from './Textarea.module.css';

export interface TextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'maxLength' | 'onChange' | 'value'> {
  ariaLabel: string;
  /** Figma Error 状态：边框转为危险色，提示文案同步变红。 */
  error?: boolean;
  /**
   * 字数上限，按 Ant Design `count.max` 口径：**只把计数标红，不截断输入**。
   * 因此它不会透传到原生 `maxLength`。
   */
  maxLength?: number;
  onValueChange: (value: string) => void;
  /** Figma Show-prompt：输入框下方的提示文案，可放链接等节点。 */
  prompt?: ReactNode;
  /** Figma Resizer：允许右下角拖拽改高（原生 resize 手柄），默认开启。 */
  resizable?: boolean;
  /** 是否显示计数；给了 `maxLength` 时显示为「已输入/上限」。 */
  showCount?: boolean;
  value: string;
}

export function Textarea({ ariaLabel, className, error = false, maxLength, onValueChange, prompt, resizable = true, showCount = false, value, ...textareaProps }: TextareaProps) {
  const exceed = maxLength !== undefined && value.length > maxLength;

  const box = (
    <span className={[styles.box, className].filter(Boolean).join(' ')} data-error={error || undefined} data-resizable={resizable || undefined}>
      {resizable ? <span aria-hidden="true" className={styles.resizer}><img alt="" src={resizerSrc} /></span> : null}
      <textarea aria-label={ariaLabel} className={styles.control} onChange={(event) => onValueChange(event.target.value)} value={value} {...textareaProps} />
      {showCount
        ? <span className={styles.count} data-exceed={exceed || undefined}>{maxLength === undefined ? `${value.length}` : `${value.length}/${maxLength}`}</span>
        : null}
    </span>
  );

  if (!prompt) return box;

  return <span className={styles.root}>{box}<span className={styles.prompt}>{prompt}</span></span>;
}
